import React, { useState, useEffect, useMemo } from "react";
import useHttp from "../../../hooks/use-http";
import { Spin, Select } from "antd";
import moment from "moment";

import DynamicPieChart from "../../pieChart";
import DynamicColumnChart from "../../columnChart";
import DynamicBarChart from "../../barChart";
import DynamicLineChart from "../../lineChart";

const { Option } = Select;

const FieldVisitCount = ({ isMaximized, openMaximize }) => {
  const [loading, setLoading] = useState(true);

  const [totalFieldVisits, setTotalFieldVisits] = useState(0);
  const [visitsByDate, setVisitsByDate] = useState([]);
  const [visitsByUser, setVisitsByUser] = useState([]);

  const [viewType, setViewType] = useState("date"); 
  const [chartType, setChartType] = useState("pie"); 

  const [fromDate, setFromDate] = useState("all");
  const [toDate, setToDate] = useState("all");

  const [selectedUser, setSelectedUser] = useState("all");

  const { sendRequest: fetchData } = useHttp();

  useEffect(() => {
    const orgId = localStorage.getItem("orgId") || "";
    const url = `/api/fieldVisitCountAgriPromoter/?orgId=${orgId}`;

    fetchData({ url }, (res) => {
      setTotalFieldVisits(res?.totalFieldVisits || 0);
      setVisitsByDate(res?.visitsByDate || []);
      setVisitsByUser(res?.visitsByUser || []);
      setLoading(false);
    });
  }, [fetchData]);

  const dateOptions = useMemo(() => {
    const arr = (visitsByDate || []).map((d) => d.date).filter(Boolean);
    const unique = Array.from(new Set(arr)).sort();
    return unique;
  }, [visitsByDate]);

  const filteredVisitsByDate = useMemo(() => {
    if (!visitsByDate.length) return [];

    if (fromDate === "all" && toDate === "all") return visitsByDate;

    const defaultStart = visitsByDate[0]?.date;
    const defaultEnd = visitsByDate[visitsByDate.length - 1]?.date;

    const start = fromDate === "all" ? defaultStart : fromDate;
    const end = toDate === "all" ? defaultEnd : toDate;

    const rangeStart = start <= end ? start : end;
    const rangeEnd = end >= start ? end : start;

    return visitsByDate.filter(
      (row) => row.date >= rangeStart && row.date <= rangeEnd
    );
  }, [visitsByDate, fromDate, toDate]);

  const filteredVisitsByUser = useMemo(() => {
    if (selectedUser === "all") return visitsByUser;
    return visitsByUser.filter(
      (u) => String(u.userId) === String(selectedUser)
    );
  }, [visitsByUser, selectedUser]);

  let chartDataset = [];
  let displayValue = totalFieldVisits;

  if (viewType === "date") {
    chartDataset = filteredVisitsByDate;
    displayValue = filteredVisitsByDate.reduce(
      (sum, r) => sum + (r.fieldVisitCount || 0),
      0
    );
  } else {
    chartDataset = filteredVisitsByUser;
    displayValue = filteredVisitsByUser.reduce(
      (sum, r) => sum + (r.fieldVisitCount || 0),
      0
    );
  }

  const noData =
    !loading && !visitsByDate.length && !visitsByUser.length;

  if (isMaximized) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          padding: 20,
          background: "#f5f7ff",
          borderRadius: 12,
          boxSizing: "border-box",
        }}
      >
        <div
          className="flex items-center mb-4"
          style={{ gap: 10, flexWrap: "wrap" }}
        >
          <Select
            value={viewType}
            style={{ width: 140 }}
            onChange={(v) => {
              setViewType(v);
              setFromDate("all");
              setToDate("all");
              setSelectedUser("all");
            }}
          >
            <Option value="date">Date Wise</Option>
            <Option value="user">User Wise</Option>
          </Select>

          {viewType === "date" && (
            <>
              <Select
                value={fromDate}
                style={{ width: 130, marginLeft: 5, marginRight: 5 }}
                onChange={(v) => setFromDate(v)}
              >
                <Option value="all">From: All</Option>
                {dateOptions.map((d) => (
                  <Option key={d} value={d}>
                    {moment(d).format("DD/MM/YYYY")}
                  </Option>
                ))}
              </Select>

              <Select
                value={toDate}
                style={{ width: 130, marginRight: 5 }}
                onChange={(v) => setToDate(v)}
              >
                <Option value="all">To: All</Option>
                {dateOptions.map((d) => (
                  <Option key={d} value={d}>
                    {moment(d).format("DD/MM/YYYY")}
                  </Option>
                ))}
              </Select>
            </>
          )}

          {viewType === "user" && (
            <Select
              value={selectedUser}
              style={{ width: 220, marginLeft: 5, marginRight: 5 }}
              onChange={(v) => setSelectedUser(v)}
            >
              <Option value="all">All Users</Option>
              {visitsByUser.map((u) => (
                <Option key={u.userId} value={u.userId}>
                  {u.userName || `User ${u.userId}`}
                </Option>
              ))}
            </Select>
          )}

          <Select
            value={chartType}
            style={{ width: 160, marginLeft: "auto" }}
            onChange={(v) => setChartType(v)}
          >
            <Option value="pie">Pie Chart</Option>
            <Option value="column">Column Chart</Option>
            <Option value="bar">Bar Chart</Option>
            <Option value="line">Line Chart</Option>
          </Select>
        </div>

        {loading ? (
          <div
            style={{
              height: "calc(100% - 80px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin />
          </div>
        ) : noData ? (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "#64748b",
            }}
          >
            <h3 style={{ marginBottom: 4 }}>No Field Visit Data</h3>
            <p style={{ margin: 0 }}>Try changing filters or date range.</p>
          </div>
        ) : (
          <>
            <div
              style={{
                textAlign: "center",
                marginBottom: 16,
                padding: 12,
                background: "#e0ebff",
                borderRadius: 10,
              }}
            >
              <h1
                style={{
                  fontSize: 34,
                  fontWeight: 700,
                  color: "#1e3a8a",
                  marginBottom: 4,
                }}
              >
                {Number(displayValue || 0).toLocaleString()}
              </h1>
              <p
                style={{
                  fontSize: 16,
                  color: "#1e3a8a",
                  margin: 0,
                }}
              >
                {viewType === "date"
                  ? "Total Field Visits (Filtered by Date)"
                  : selectedUser === "all"
                    ? "Total Field Visits (All Users)"
                    : "Total Field Visits (Selected User)"}
              </p>
            </div>

            <div style={{ height: "100%", overflow: "hidden" }}>
              {viewType === "date" && (
                <>
                  {chartType === "pie" && (
                    <DynamicPieChart
                      data={chartDataset}
                      angleField="fieldVisitCount"
                      colorField="date"
                      isMaximized={true}
                    />
                  )}

                  {chartType === "column" && (
                    <DynamicColumnChart
                      data={chartDataset}
                      xField="date"
                      yField="fieldVisitCount"
                      seriesField="date"
                      xAxisTitle="Date"
                      yAxisTitle="Field Visits"
                    />
                  )}

                  {chartType === "bar" && (
                    <DynamicBarChart
                      data={chartDataset}
                      xField="date"
                      yField="fieldVisitCount"
                      seriesField="date"
                      xAxisTitle="Date"
                      yAxisTitle="Field Visits"
                    />
                  )}

                  {chartType === "line" && (
                    <DynamicLineChart
                      data={chartDataset}
                      xField="date"
                      yField="fieldVisitCount"
                      seriesField="date"
                      xAxisTitle="Date"
                      yAxisTitle="Field Visits"
                    />
                  )}
                </>
              )}

              {viewType === "user" && (
                <>
                  {chartType === "pie" && (
                    <DynamicPieChart
                      data={chartDataset}
                      angleField="fieldVisitCount"
                      colorField="userName"
                      isMaximized={true}
                    />
                  )}

                  {chartType === "column" && (
                    <DynamicColumnChart
                      data={chartDataset}
                      xField="userName"
                      yField="fieldVisitCount"
                      seriesField="userName"
                      xAxisTitle="User"
                      yAxisTitle="Field Visits"
                    />
                  )}

                  {chartType === "bar" && (
                    <DynamicBarChart
                      data={chartDataset}
                      xField="userName"
                      yField="fieldVisitCount"
                      seriesField="userName"
                      xAxisTitle="User"
                      yAxisTitle="Field Visits"
                    />
                  )}

                  {chartType === "line" && (
                    <DynamicLineChart
                      data={chartDataset}
                      xField="userName"
                      yField="fieldVisitCount"
                      seriesField="userName"
                      xAxisTitle="User"
                      yAxisTitle="Field Visits"
                    />
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl shadow-md"
      style={{
        background: "#f3e8d3",
        height: 200,
        width: "100%",
        padding: 18,
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <Spin />
        </div>
      ) : noData ? (
        <div
          className="flex justify-center items-center h-full"
          style={{ color: "#3d5c26" }}
        >
          <div style={{ textAlign: "center" }}>
            <h3 style={{ margin: 0 }}>No Field Visits</h3>
            <p style={{ marginTop: 4, fontSize: 13 }}>
              No records found
            </p>
          </div>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                // fontSize: 32,
                fontWeight: 700,
                color: "#3d5c26",
                marginBottom: 4,
              }}
            >
              {Number(totalFieldVisits || 0).toLocaleString()}
            </h1>
            <h3
              style={{
                // fontSize: 16,
                fontWeight: 700,
                margin: 0,
                color: "#3d5c26",
              }}
            >
              Total Field Visits
            </h3>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: 8,
              right: 18,
              cursor: "pointer",
              fontWeight: 600,
              color: "#3d5c26",
              fontSize: 14,
            }}
            onClick={openMaximize}
          >
            More...
          </div>
        </>
      )}
    </div>
  );
};

export default FieldVisitCount;
